import { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import {
  GraduationCap, Target, Buildings, CurrencyInr, ChartLine,
  ChatCircle, Clock, ShieldCheck, ArrowRight, Phone, EnvelopeSimple,
  MapPin, WhatsappLogo, Moon, Sun, List, X
} from '@phosphor-icons/react';
import { useTheme } from '@/contexts/ThemeContext';
import api from '@/lib/api';

gsap.registerPlugin(ScrollTrigger);

// Why-choose-us value props for prospective students/parents visiting the
// institute's own public home page (this is NOT a CRM sales pitch — see ADM-28).
const FEATURES = [
  { icon: GraduationCap, title: 'Experienced Faculty', desc: 'Learn from mentors who know how to get real results, not just cover the syllabus.', color: '#2B6EF0' },
  { icon: Target, title: 'Personalized Mentorship', desc: 'Every student gets a learning plan built around their pace, goals, and gaps.', color: '#8B5CF6' },
  { icon: Buildings, title: 'Modern Campuses', desc: 'Well-equipped classrooms and study spaces designed for focus, close to home.', color: '#10A882' },
  { icon: CurrencyInr, title: 'Flexible Fee Plans', desc: 'Transparent fees with easy installment options — no hidden costs, ever.', color: '#E5A100' },
  { icon: ChartLine, title: 'Proven Track Record', desc: "Consistent results year after year, backed by outcomes we're happy to share.", color: '#E04B2A' },
  { icon: ChatCircle, title: 'Always Reachable', desc: 'WhatsApp, call, or email — our team responds fast, every time you reach out.', color: '#06B6D4' },
  { icon: Clock, title: 'Flexible Timings', desc: 'Batches designed to fit around school, college, and other commitments.', color: '#F97316' },
  { icon: ShieldCheck, title: 'Trusted by Parents', desc: 'Transparent processes and clear communication, every step of the way.', color: '#EC4899' },
];

// Public-facing showcase of the institute itself — programs, faculty, campus,
// outcomes. Deliberately does NOT show internal dashboard/pipeline/fee-collection
// tooling (that's staff-only). Image paths are placeholders, same pre-existing
// convention as the rest of the app (may or may not exist as static assets yet).
const SHOWCASE_ITEMS = [
  { src: '/programs/overview.jpg', label: 'Overview', desc: 'A quick look at what makes learning here different.' },
  { src: '/programs/curriculum.jpg', label: 'Curriculum', desc: 'Structured, exam-focused curriculum built by experienced educators.' },
  { src: '/programs/faculty.jpg', label: 'Faculty', desc: 'Meet the mentors guiding every student toward their goals.' },
  { src: '/programs/campus.jpg', label: 'Campus', desc: 'Modern classrooms and facilities designed for focused learning.' },
  { src: '/programs/outcomes.jpg', label: 'Outcomes', desc: 'Real results — see how our students perform, year after year.' },
];

// Institute-appropriate stats (not vendor bragging stats like "integrations").
// Fallback-safe placeholder values — swap for real numbers when available.
const STATS = [
  { value: 10, suffix: '+', label: 'Years of Excellence' },
  { value: 5000, suffix: '+', label: 'Students Mentored' },
  { value: 15, suffix: '+', label: 'Programs Offered' },
  { value: 95, suffix: '%', label: 'Result Rate' },
];

const DEFAULT_ORG = { name: 'YuHasPro', logo_url: '', website: '', phone: '', email: '' };

export default function LandingPage() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [activeShot, setActiveShot] = useState(0);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [org, setOrg] = useState(DEFAULT_ORG);
  const containerRef = useRef(null);
  const heroRef = useRef(null);
  const featuresRef = useRef(null);
  const screenshotRef = useRef(null);
  const statsRef = useRef(null);
  const reachRef = useRef(null);
  const pricingRef = useRef(null);

  // ADM-28: this institute's own branded gateway page pulls org profile (name/logo/
  // contact) from a settings endpoint being built in parallel on another ticket. It
  // may not exist yet, so this must never block rendering — render the YuHasPro
  // fallback immediately and swap in real data if/when the response arrives.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/api/settings/organization');
        if (cancelled || !data) return;
        setOrg(prev => ({
          name: data.name || prev.name,
          logo_url: data.logo_url || prev.logo_url,
          website: data.website || prev.website,
          phone: data.phone || prev.phone,
          email: data.email || prev.email,
        }));
      } catch (e) {
        // Endpoint may not exist yet, or may fail/be slow — silently keep the
        // YuHasPro fallback rather than blocking or erroring the public page.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const orgInitial = (org.name || 'Y').charAt(0).toUpperCase();
  const orgHost = (org.website || 'www.yuhaspro.in').replace(/^https?:\/\//, '').replace(/\/$/, '');

  const reachChannels = [
    { name: 'WhatsApp', label: 'Message Us', color: '#25D366', tx: -160, ty: -90, Icon: WhatsappLogo },
    { name: 'Call', label: 'Call Us', color: '#2B6EF0', tx: 160, ty: -90, Icon: Phone },
    { name: 'Email', label: 'Email Us', color: '#E5A100', tx: -160, ty: 90, Icon: EnvelopeSimple },
    { name: 'Visit', label: 'Visit Campus', color: '#10A882', tx: 160, ty: 90, Icon: MapPin },
  ];

  useGSAP(() => {
    const ctx = gsap.context(() => {
      // Hero entrance
      const heroTl = gsap.timeline({ defaults: { ease: 'power3.out' } });
      heroTl
        .from('.hero-badge', { y: 20, opacity: 0, duration: 0.6 })
        .from('.hero-title-line', { y: 60, opacity: 0, stagger: 0.12, duration: 0.8 }, '-=0.3')
        .from('.hero-subtitle', { y: 20, opacity: 0, duration: 0.6 }, '-=0.4')
        .from('.hero-ctas', { y: 20, opacity: 0, duration: 0.5 }, '-=0.3')
        .from('.hero-image', { y: 40, opacity: 0, scale: 0.96, duration: 1, ease: 'power2.out' }, '-=0.4')
        .from('.hero-stat', { y: 20, opacity: 0, stagger: 0.08, duration: 0.5 }, '-=0.6');

      // Hero image parallax
      gsap.to('.hero-image', {
        yPercent: -6,
        ease: 'none',
        scrollTrigger: { trigger: heroRef.current, start: 'top top', end: 'bottom top', scrub: 1 },
      });

      // Features stagger
      gsap.from('.feature-card', {
        y: 40, opacity: 0, stagger: 0.08, duration: 0.6, immediateRender: false,
        scrollTrigger: { trigger: featuresRef.current, start: 'top 80%' },
      });

      // Showcase section
      gsap.from('.screenshot-browser', {
        y: 60, opacity: 0, scale: 0.95, duration: 1, immediateRender: false,
        scrollTrigger: { trigger: screenshotRef.current, start: 'top 75%' },
      });

      // Stats counters
      if (statsRef.current) {
        const counters = statsRef.current.querySelectorAll('.stat-value');
        counters.forEach(el => {
          const target = parseFloat(el.dataset.value);
          gsap.fromTo(el, { textContent: 0 }, {
            textContent: target,
            duration: 2,
            ease: 'power2.out',
            snap: { textContent: target % 1 === 0 ? 1 : 0.1 },
            scrollTrigger: { trigger: el, start: 'top 85%' },
            onUpdate: function() {
              const val = parseFloat(gsap.getProperty(el, 'textContent'));
              el.textContent = target % 1 === 0 ? Math.round(val).toLocaleString('en-IN') : val.toFixed(1);
            }
          });
        });
      }

      // Reach channels — spring-in from center
      gsap.from('.integration-node', {
        scale: 0, opacity: 0, stagger: 0.12, duration: 0.8, ease: 'back.out(1.7)', immediateRender: false,
        scrollTrigger: { trigger: reachRef.current, start: 'top 75%' },
      });

      // Enquiry CTA
      gsap.from('.pricing-card', {
        y: 40, opacity: 0, stagger: 0.1, duration: 0.6, immediateRender: false,
        scrollTrigger: { trigger: pricingRef.current, start: 'top 80%' },
      });
    }, containerRef);

    return () => ctx.revert();
  }, { scope: containerRef });

  return (
    <div ref={containerRef} className="bg-background text-foreground overflow-x-hidden" data-testid="landing-page">
      {/* ═══ NAVBAR ═══ */}
      <header>
      <nav role="navigation" aria-label="Main navigation" className="fixed top-0 left-0 right-0 z-50 h-16 flex items-center justify-between px-6 lg:px-12 border-b border-transparent" style={{ backdropFilter: 'blur(24px) saturate(1.3)', WebkitBackdropFilter: 'blur(24px) saturate(1.3)', backgroundColor: theme === 'dark' ? 'rgba(15,15,15,0.85)' : 'rgba(247,246,243,0.85)' }} data-testid="landing-nav">
        <div className="flex items-center gap-2.5">
          {org.logo_url ? (
            <img src={org.logo_url} alt={org.name} className="w-8 h-8 rounded-lg object-cover" />
          ) : (
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#E04B2A' }}>
              <span className="text-xs font-extrabold text-white font-display">{orgInitial}</span>
            </div>
          )}
          <span className="font-display font-bold text-lg tracking-tight text-foreground">{org.name}</span>
        </div>
        <div className="hidden md:flex items-center gap-8">
          <a href="#features" className="text-[13px] text-muted-foreground hover:text-foreground transition-colors">Why Us</a>
          <a href="#screenshots" className="text-[13px] text-muted-foreground hover:text-foreground transition-colors">Programs</a>
          <Link to="/docs" className="text-[13px] text-muted-foreground hover:text-foreground transition-colors">Docs</Link>
          <a href="#integrations" className="text-[13px] text-muted-foreground hover:text-foreground transition-colors">Contact</a>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={toggleTheme} className="p-2 hover:bg-secondary rounded-lg transition-colors text-muted-foreground" data-testid="landing-theme-toggle">
            {theme === 'dark' ? <Sun size={16} weight="bold" /> : <Moon size={16} weight="bold" />}
          </button>
          <button onClick={() => navigate('/login')} className="text-[13px] text-muted-foreground hover:text-foreground transition-colors hidden sm:block" data-testid="nav-signin">Sign in</button>
          <button onClick={() => navigate('/contact')} className="hidden sm:flex h-9 px-5 rounded-lg text-[13px] font-bold text-white items-center gap-1.5 transition-transform hover:scale-[1.02]" style={{ backgroundColor: '#E04B2A' }} data-testid="nav-get-started">Enquire Now <ArrowRight size={14} weight="bold" /></button>
          <button onClick={() => setMobileMenu(!mobileMenu)} className="md:hidden p-2 text-foreground">
            {mobileMenu ? <X size={20} /> : <List size={20} />}
          </button>
        </div>
      </nav>
      {mobileMenu && (
        <div className="fixed inset-0 z-40 bg-background pt-20 px-6 flex flex-col gap-6 md:hidden">
          <a href="#features" onClick={() => setMobileMenu(false)} className="text-lg font-display font-bold">Why Us</a>
          <a href="#screenshots" onClick={() => setMobileMenu(false)} className="text-lg font-display font-bold">Programs</a>
          <Link to="/contact" onClick={() => setMobileMenu(false)} className="text-lg font-display font-bold">Contact</Link>
          <button onClick={() => { navigate('/contact'); setMobileMenu(false); }} className="h-12 rounded-lg text-sm font-bold text-white mt-4" style={{ backgroundColor: '#E04B2A' }}>Enquire Now</button>
        </div>
      )}
      </header>

      <main>
      {/* ═══ HERO ═══ */}
      <section ref={heroRef} className="pt-32 pb-12 px-6 lg:px-12 max-w-7xl mx-auto" data-testid="hero-section">
        <div className="max-w-3xl">
          <div className="hero-badge inline-flex items-center gap-2 px-3 py-1 rounded-full border border-border text-[11px] font-mono uppercase tracking-wider text-muted-foreground mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Admissions Open
          </div>
          <h1 className="font-display font-extrabold tracking-[-0.03em] leading-[1.05]">
            <span className="hero-title-line block text-[clamp(2.5rem,5.5vw,5rem)]">Begin Your Journey</span>
            <span className="hero-title-line block text-[clamp(2.5rem,5.5vw,5rem)]" style={{ color: '#E04B2A' }}>With {org.name}.</span>
          </h1>
          <p className="hero-subtitle text-lg text-muted-foreground mt-6 max-w-xl leading-relaxed">
            Personalized mentorship, expert faculty, and a curriculum built for real results.
            Explore our programs or talk to our admissions team today.
          </p>
          <div className="hero-ctas flex items-center gap-3 mt-8">
            <button onClick={() => navigate('/contact')} className="h-12 px-7 rounded-lg text-sm font-bold text-white flex items-center gap-2 transition-transform hover:scale-[1.02] active:scale-[0.98]" style={{ backgroundColor: '#E04B2A' }} data-testid="hero-cta-primary">
              Start Your Enquiry <ArrowRight size={16} weight="bold" />
            </button>
            <a href="#screenshots" className="h-12 px-7 rounded-lg text-sm font-semibold border border-border text-foreground flex items-center gap-2 hover:bg-secondary transition-colors" data-testid="hero-cta-secondary">
              Explore Programs
            </a>
          </div>
          <div className="flex gap-8 mt-10">
            {[['10+', 'Years'], ['5000+', 'Students'], ['95%', 'Results']].map(([v, l]) => (
              <div key={l} className="hero-stat">
                <p className="font-display text-2xl font-extrabold text-foreground">{v}</p>
                <p className="text-[10px] text-muted-foreground font-mono uppercase tracking-wider mt-0.5">{l}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="hero-image mt-14 relative">
          <div className="rounded-xl overflow-hidden border border-border shadow-2xl" style={{ boxShadow: theme === 'dark' ? '0 40px 80px -20px rgba(0,0,0,0.5)' : '0 40px 80px -20px rgba(0,0,0,0.15)' }}>
            <div className="h-8 bg-card border-b border-border flex items-center px-4 gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-red-400" /><span className="w-2.5 h-2.5 rounded-full bg-yellow-400" /><span className="w-2.5 h-2.5 rounded-full bg-green-400" />
              <span className="text-[10px] font-mono text-muted-foreground ml-3">{orgHost}</span>
            </div>
            <img src="/campus/hero.jpg" alt={`${org.name} campus`} className="w-full" loading="eager" />
          </div>
        </div>
      </section>

      {/* ═══ FEATURES ═══ */}
      <section id="features" ref={featuresRef} className="py-24 px-6 lg:px-12 max-w-7xl mx-auto" data-testid="features-section">
        <div className="max-w-xl mb-14">
          <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground mb-3" style={{ color: '#E04B2A' }}>WHY US</p>
          <h2 className="font-display text-4xl font-extrabold text-foreground tracking-tight leading-[1.1]">Everything you need to<br />succeed, in one place.</h2>
          <p className="text-base text-muted-foreground mt-4 leading-relaxed">From your first enquiry to graduation day — {org.name} is with you at every step.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="feature-card p-5 rounded-xl border border-border bg-card hover:border-foreground/10 transition-all duration-300 group block" data-testid={`feature-${f.title.toLowerCase().replace(/ /g, '-')}`}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-4 transition-transform group-hover:scale-110" style={{ backgroundColor: `${f.color}12`, color: f.color }}>
                  <Icon size={18} weight="bold" />
                </div>
                <h3 className="font-display text-sm font-bold text-foreground mb-1.5">{f.title}</h3>
                <p className="text-[12px] text-muted-foreground leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ═══ PROGRAMS SHOWCASE ═══ */}
      <ProgramsShowcase theme={theme} org={org} orgHost={orgHost} screenshotRef={screenshotRef} activeShot={activeShot} setActiveShot={setActiveShot} />

      {/* ═══ STATS ═══ */}
      <section ref={statsRef} className="py-20 border-y border-border" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <div key={s.label} className="text-center">
              <p className="font-display text-5xl font-extrabold text-foreground">
                <span className="stat-value" data-value={s.value}>0</span><span className="text-3xl" style={{ color: '#E04B2A' }}>{s.suffix}</span>
              </p>
              <p className="text-[11px] font-mono text-muted-foreground uppercase tracking-wider mt-2">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ REACH US ═══ */}
      <section id="integrations" ref={reachRef} className="py-28 px-6 lg:px-12 relative overflow-hidden" data-testid="integrations-section">
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <p className="text-[10px] font-mono uppercase tracking-[0.15em] mb-3" style={{ color: '#E04B2A' }}>REACH US</p>
            <h2 className="font-display text-4xl font-extrabold text-foreground tracking-tight leading-[1.1]">However you prefer,<br />we're here.</h2>
            <p className="text-base text-muted-foreground mt-4 max-w-lg mx-auto">
              {org.phone || org.email
                ? <>WhatsApp or call {org.phone || 'us'}{org.email ? <>, email <a href={`mailto:${org.email}`} className="underline hover:text-foreground">{org.email}</a></> : ''}, or visit our campus — whichever works best for you.</>
                : 'WhatsApp, call, email, or visit our campus — whichever works best for you.'}
            </p>
          </div>

          {/* Desktop: Orbital hub */}
          <div className="hidden md:flex items-center justify-center relative" style={{ height: '380px' }}>
            {/* Orbit rings */}
            <div className="absolute w-[280px] h-[280px] rounded-full border border-border/30" style={{ animation: 'spin-slow 60s linear infinite' }} />
            <div className="absolute w-[480px] h-[480px] rounded-full border border-border/15" />

            {/* Pulse ring */}
            <div className="absolute w-[280px] h-[280px] rounded-full" style={{ animation: 'pulse-ring 3s ease-in-out infinite', border: '1px solid rgba(224,75,42,0.15)' }} />

            {/* Center hub */}
            <div className="integration-node relative z-20">
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #E04B2A, #C93E22)', boxShadow: '0 0 60px rgba(224,75,42,0.25), 0 0 120px rgba(224,75,42,0.1)' }}>
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none"><rect x="4" y="4" width="24" height="24" rx="4" fill="white" fillOpacity="0.2"/><text x="16" y="22" textAnchor="middle" fill="white" fontSize="16" fontWeight="800" fontFamily="system-ui">{orgInitial}</text></svg>
              </div>
            </div>

            {/* Satellite nodes — positioned with transform from center */}
            {reachChannels.map((ch, i) => (
              <div key={ch.name} className="absolute z-10 integration-node group" style={{ transform: `translate(${ch.tx}px, ${ch.ty}px)` }} data-testid={`integration-${ch.name.toLowerCase()}`}>
                {/* Connection line to center */}
                <svg className="absolute pointer-events-none" style={{ left: '50%', top: '50%', width: '1px', height: '1px', overflow: 'visible' }}>
                  <line x1="0" y1="0" x2={-ch.tx} y2={-ch.ty} stroke={theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'} strokeWidth="1" strokeDasharray="3 6">
                    <animate attributeName="stroke-dashoffset" from="9" to="0" dur={`${1.5 + i * 0.2}s`} repeatCount="indefinite" />
                  </line>
                </svg>
                <div className="flex flex-col items-center gap-2.5 relative">
                  {/* Glow */}
                  <div className="absolute -inset-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl" style={{ backgroundColor: `${ch.color}30` }} />
                  {/* Node */}
                  <div className="relative w-16 h-16 rounded-full border bg-card flex items-center justify-center transition-all duration-300 group-hover:scale-110" style={{ borderColor: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)', boxShadow: `0 4px 20px ${ch.color}10` }}>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${ch.color}12` }}>
                      <ch.Icon size={22} weight="fill" style={{ color: ch.color }} />
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-[11px] font-bold text-foreground whitespace-nowrap">{ch.name}</p>
                    <p className="text-[8px] text-muted-foreground font-mono uppercase tracking-[0.15em]">{ch.label}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile: card grid */}
          <div className="md:hidden grid grid-cols-2 gap-3">
            {reachChannels.map((ch) => (
              <div key={ch.name} className="integration-node p-4 rounded-xl border border-border bg-card flex items-center gap-3 group hover:border-foreground/15 transition-all">
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110" style={{ backgroundColor: `${ch.color}12` }}>
                  <ch.Icon size={18} weight="fill" style={{ color: ch.color }} />
                </div>
                <div>
                  <p className="text-[12px] font-bold text-foreground">{ch.name}</p>
                  <p className="text-[9px] text-muted-foreground font-mono uppercase tracking-[0.12em]">{ch.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <style>{`
          @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          @keyframes pulse-ring { 0%, 100% { transform: scale(1); opacity: 0.3; } 50% { transform: scale(1.08); opacity: 0.1; } }
        `}</style>
      </section>

      {/* ═══ ENQUIRY CTA (replaces pricing) ═══ */}
      <section id="pricing" ref={pricingRef} className="py-24 px-6 lg:px-12 max-w-7xl mx-auto" data-testid="pricing-section">
        <div className="pricing-card max-w-3xl mx-auto text-center rounded-2xl p-12 border border-border relative overflow-hidden" style={{ background: theme === 'dark' ? 'linear-gradient(135deg, rgba(224,75,42,0.08), transparent)' : 'linear-gradient(135deg, rgba(224,75,42,0.05), transparent)' }}>
          <p className="text-[10px] font-mono uppercase tracking-[0.15em] mb-4" style={{ color: '#E04B2A' }}>GET STARTED</p>
          <h2 className="font-display text-4xl font-extrabold text-foreground tracking-tight leading-[1.1]">Ready to begin your<br />admissions journey?</h2>
          <p className="text-base text-muted-foreground mt-4 max-w-md mx-auto">Talk to our admissions team — we'll guide you through programs, fees, and next steps.</p>
          <div className="flex justify-center gap-3 mt-8">
            <button onClick={() => navigate('/contact')} className="h-12 px-8 rounded-lg text-sm font-bold text-white flex items-center gap-2 transition-transform hover:scale-[1.02]" style={{ backgroundColor: '#E04B2A' }} data-testid="cta-contact-sales">
              Enquire Now <ArrowRight size={16} weight="bold" />
            </button>
          </div>
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section className="py-24 px-6 lg:px-12" data-testid="cta-section">
        <div className="max-w-3xl mx-auto text-center rounded-2xl p-12 border border-border relative overflow-hidden" style={{ background: theme === 'dark' ? 'linear-gradient(135deg, rgba(224,75,42,0.08), transparent)' : 'linear-gradient(135deg, rgba(224,75,42,0.05), transparent)' }}>
          <h2 className="font-display text-4xl font-extrabold text-foreground tracking-tight leading-[1.1]">See {org.name}<br />up close.</h2>
          <p className="text-base text-muted-foreground mt-4 max-w-md mx-auto">Book a campus visit or a personalized counselling session with our admissions team.</p>
          <div className="flex justify-center gap-3 mt-8">
            <button onClick={() => navigate('/contact')} className="h-12 px-8 rounded-lg text-sm font-bold text-white flex items-center gap-2 transition-transform hover:scale-[1.02]" style={{ backgroundColor: '#E04B2A' }} data-testid="cta-final">
              Book a Counselling Session <ArrowRight size={16} weight="bold" />
            </button>
          </div>
        </div>
      </section>

      </main>
      {/* ═══ FOOTER ═══ */}
      <footer className="border-t border-border py-12 px-6 lg:px-12" data-testid="footer">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-4">
              {org.logo_url ? (
                <img src={org.logo_url} alt={org.name} className="w-7 h-7 rounded-lg object-cover" />
              ) : (
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#E04B2A' }}>
                  <span className="text-[10px] font-extrabold text-white font-display">{orgInitial}</span>
                </div>
              )}
              <span className="font-display font-bold text-sm text-foreground">{org.name}</span>
            </div>
            <p className="text-[11px] text-muted-foreground leading-relaxed">Guiding students toward their best future.</p>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-3 font-bold">Institute</p>
            <div className="space-y-2 text-[12px] text-muted-foreground">
              <a href="#features" className="block hover:text-foreground transition-colors">Why Us</a>
              <a href="#screenshots" className="block hover:text-foreground transition-colors">Programs</a>
              <Link to="/docs" className="block hover:text-foreground transition-colors">Resources</Link>
              <Link to="/contact" className="block hover:text-foreground transition-colors">Contact</Link>
            </div>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-3 font-bold">Get in Touch</p>
            <div className="space-y-2 text-[12px] text-muted-foreground">
              {org.phone ? <a href={`tel:${org.phone}`} className="block hover:text-foreground transition-colors">{org.phone}</a> : <span className="block">Call our admissions desk</span>}
              {org.email ? <a href={`mailto:${org.email}`} className="block hover:text-foreground transition-colors">{org.email}</a> : <span className="block">Email us</span>}
              <span className="block">Visit our campus</span>
            </div>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-3 font-bold">Legal</p>
            <div className="space-y-2 text-[12px] text-muted-foreground">
              <Link to="/privacy" className="block hover:text-foreground transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="block hover:text-foreground transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-8 pt-6 border-t border-border flex items-center justify-between">
          <p className="text-[10px] text-muted-foreground font-mono">&copy; 2026 {org.name}. All rights reserved.</p>
          <p className="text-[10px] text-muted-foreground font-mono">Made in India</p>
        </div>
      </footer>
    </div>
  );
}

function ProgramsShowcase({ theme, org, orgHost, screenshotRef, activeShot, setActiveShot }) {
  const prevShotRef = useRef(activeShot);
  const autoplayRef = useRef(null);
  const [loadedMap, setLoadedMap] = useState({});

  // Progressive image loading — first image eager, rest in background
  useEffect(() => {
    SHOWCASE_ITEMS.forEach((s, i) => {
      const img = new Image();
      img.onload = () => setLoadedMap(prev => ({ ...prev, [i]: true }));
      img.src = s.src;
    });
  }, []);

  const allLoaded = Object.keys(loadedMap).length === SHOWCASE_ITEMS.length;
  const activeLoaded = !!loadedMap[activeShot];

  // Start autoplay only after first image is loaded
  useEffect(() => {
    if (!loadedMap[0]) return;
    autoplayRef.current = setInterval(() => {
      setActiveShot(prev => (prev + 1) % SHOWCASE_ITEMS.length);
    }, 5000);
    return () => clearInterval(autoplayRef.current);
  }, [loadedMap, setActiveShot]);

  const handleTabClick = useCallback((i) => {
    clearInterval(autoplayRef.current);
    setActiveShot(i);
    autoplayRef.current = setInterval(() => {
      setActiveShot(prev => (prev + 1) % SHOWCASE_ITEMS.length);
    }, 5000);
  }, [setActiveShot]);

  useEffect(() => { prevShotRef.current = activeShot; }, [activeShot]);

  const shot = SHOWCASE_ITEMS[activeShot];
  const loadProgress = Math.round((Object.keys(loadedMap).length / SHOWCASE_ITEMS.length) * 100);

  return (
    <section id="screenshots" ref={screenshotRef} className="py-24 px-6 lg:px-12 max-w-7xl mx-auto" data-testid="screenshots-section">
      <div className="text-center max-w-xl mx-auto mb-14">
        <p className="text-[10px] font-mono uppercase tracking-[0.15em] mb-3" style={{ color: '#E04B2A' }}>PROGRAMS</p>
        <h2 className="font-display text-4xl font-extrabold text-foreground tracking-tight leading-[1.1]">Explore life at {org.name}</h2>
        <p className="text-base text-muted-foreground mt-4">A closer look at our curriculum, faculty, campus, and the outcomes we're proud of.</p>
      </div>

      {/* Tabs */}
      <div className="flex flex-col items-center gap-4 mb-10">
        <div className="flex flex-wrap justify-center gap-1.5">
          {SHOWCASE_ITEMS.map((s, i) => (
            <button key={s.label} onClick={() => handleTabClick(i)} data-testid={`shot-tab-${s.label.toLowerCase()}`}
              className={`relative px-4 py-2 rounded-lg text-xs font-semibold transition-all duration-300 overflow-hidden ${
                activeShot === i ? 'text-white' : 'text-muted-foreground hover:text-foreground border border-border hover:bg-secondary'
              }`}
              style={activeShot === i ? { backgroundColor: '#E04B2A' } : {}}
            >
              {s.label}
              {activeShot === i && (
                <span key={`prog-${activeShot}`} className="absolute bottom-0 left-0 h-[2px] bg-white/40 origin-left" style={{ animation: 'showcase-progress 5s linear forwards', width: '100%' }} />
              )}
            </button>
          ))}
        </div>
        <div className="flex gap-1.5">
          {SHOWCASE_ITEMS.map((_, i) => (
            <div key={i} className="h-1 rounded-full transition-all duration-500 ease-out" style={{
              width: activeShot === i ? '24px' : '6px',
              backgroundColor: activeShot === i ? '#E04B2A' : theme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
            }} />
          ))}
        </div>
      </div>

      {/* Browser mockup */}
      <div className="screenshot-browser relative" style={{ perspective: '1200px' }}>
        <div className="rounded-xl overflow-hidden border border-border" style={{
          boxShadow: theme === 'dark'
            ? '0 40px 80px -20px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.05)'
            : '0 40px 80px -20px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
          transform: 'rotateX(2deg)', transformOrigin: 'center bottom',
        }}>
          {/* Browser chrome */}
          <div className="h-10 bg-card border-b border-border flex items-center px-4 gap-2">
            <div className="flex gap-1.5">
              <span className="w-3 h-3 rounded-full bg-[#FF5F57]" />
              <span className="w-3 h-3 rounded-full bg-[#FEBC2E]" />
              <span className="w-3 h-3 rounded-full bg-[#28C840]" />
            </div>
            <div className="flex-1 mx-4">
              <div className="h-6 rounded-md bg-secondary/60 flex items-center px-3 max-w-md mx-auto">
                <svg className="w-3 h-3 text-muted-foreground/50 mr-2 flex-shrink-0" viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.5"/><path d="M11 11l3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                <span className="text-[10px] font-mono text-muted-foreground/60">{orgHost}/{shot.label.toLowerCase()}</span>
              </div>
            </div>
            {/* Loading indicator in browser chrome */}
            {!allLoaded && (
              <div className="absolute top-10 left-0 right-0 h-[2px] bg-border overflow-hidden z-10">
                <div className="h-full transition-all duration-700 ease-out" style={{ width: `${loadProgress}%`, background: 'linear-gradient(90deg, #E04B2A, #F97316)' }} />
              </div>
            )}
          </div>

          {/* Showcase area */}
          <div className="relative overflow-hidden bg-card" style={{ aspectRatio: '1440/900' }}>
            {/* Shimmer skeleton — visible until active image loads */}
            {!activeLoaded && (
              <div className="absolute inset-0 z-10">
                <div className="absolute inset-0 bg-card" />
                <div className="absolute inset-0 showcase-shimmer" style={{
                  background: `linear-gradient(90deg, transparent 0%, ${theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'} 50%, transparent 100%)`,
                  backgroundSize: '200% 100%',
                }} />
                {/* Skeleton layout mimicking a photo/media panel */}
                <div className="absolute inset-0 p-6 flex flex-col gap-4">
                  <div className="h-6 rounded bg-secondary/40 w-40" />
                  <div className="flex-1 rounded-lg bg-secondary/25" />
                  <div className="grid grid-cols-4 gap-3">
                    {[...Array(4)].map((_, i) => <div key={i} className="h-16 rounded-lg bg-secondary/30" />)}
                  </div>
                </div>
              </div>
            )}

            {/* Stacked images — slow dissolve crossfade */}
            {SHOWCASE_ITEMS.map((s, i) => (
              <img
                key={s.label}
                src={s.src}
                alt={`${org.name} — ${s.label} — ${s.desc}`}
                loading={i === 0 ? 'eager' : 'lazy'}
                style={{
                  position: i === 0 ? 'relative' : 'absolute',
                  top: 0, left: 0, width: '100%',
                  opacity: activeShot === i && loadedMap[i] ? 1 : 0,
                  transition: 'opacity 1s cubic-bezier(0.4, 0, 0.2, 1)',
                  zIndex: activeShot === i ? 2 : 1,
                }}
              />
            ))}
          </div>
        </div>

        {/* Reflection */}
        <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-3/4 h-16 rounded-full blur-2xl" style={{ backgroundColor: theme === 'dark' ? 'rgba(224,75,42,0.06)' : 'rgba(224,75,42,0.04)' }} />
      </div>

      {/* Caption */}
      <div className="mt-8 text-center" key={activeShot}>
        <h3 className="font-display text-xl font-bold text-foreground showcase-caption">{shot.label}</h3>
        <p className="text-sm text-muted-foreground mt-1.5 max-w-md mx-auto showcase-caption" style={{ animationDelay: '0.05s' }}>{shot.desc}</p>
      </div>

      <style>{`
        @keyframes showcase-progress { from { transform: scaleX(0); } to { transform: scaleX(1); } }
        .showcase-caption { animation: caption-in 0.4s ease-out both; }
        @keyframes caption-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .screenshot-browser { transition: transform 0.4s cubic-bezier(0.16,1,0.3,1); }
        .screenshot-browser:hover { transform: translateY(-4px); }
        @keyframes shimmer-slide { from { background-position: 200% 0; } to { background-position: -200% 0; } }
        .showcase-shimmer { animation: shimmer-slide 1.8s ease-in-out infinite; }
      `}</style>
    </section>
  );
}
